/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, Suspense } from "react";
import { CouponProvider } from "./Providers/Coupon";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { StaticDataProvider } from "./Providers/Data";
import { PaymentProvider } from "./Providers/Payment";
import Check from "./Pages/Check";
import { useAuth } from "./Providers/Auth";
import ProtectedRoute from "./Helpers/ProtectedRoute";
import { InfluencerProvider } from "./Providers/Influencer";
import { BrandProvider } from "./Providers/Brand";
import LoaderOne from "./Components/Loader";
import { useGeneralSettings } from "./Providers/General";
import mixpanel from "mixpanel-browser";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCookies } from "react-cookie";
import { BrandDealsProvider } from "./Providers/Deals";
import { DashboardSkeleton } from "./Components/Loader/dashboardSkeleton";
import { ExtensionProvider } from "./Providers/Extension";
import { useNotification } from "./Providers/Notification";

// Lazy load components for code splitting
const Home = React.lazy(() => import("./Pages/Dashboard/Influencer/Home"));
const Logout = React.lazy(() => import("./Pages/Logout"));
const BrandRoutes = React.lazy(
  () => import("./Pages/Brand/Routes/BrandRoutes")
);
const AdminLogin = React.lazy(() =>
  import("./Pages/Devloper/Login/Login").then((module) => ({
    default: module.AdminLogin,
  }))
);
const AdminDashBoard = React.lazy(() =>
  import("./Pages/Devloper/DashBoard.tsx/AdminDashBoard").then((module) => ({
    default: module.AdminDashBoard,
  }))
);
const InfluencerPublicProfile = React.lazy(() =>
  import("./Pages/Influencer/PublicProfile/Profile").then((module) => ({
    default: module.InfluencerPublicProfile,
  }))
);
const CollabRatingForm = React.lazy(() =>
  import("./Pages/Brand/VerifyCollab/CollabRatingForm").then((module) => ({
    default: module.CollabRatingForm,
  }))
);
const CollabRatingSuccess = React.lazy(() =>
  import("./Pages/Brand/VerifyCollab/CollabRatingSuccess").then((module) => ({
    default: module.CollabRatingSuccess,
  }))
);
const GuestSampleProfile = React.lazy(() =>
  import(
    "./Pages/Influencer/CreatorLandingPage/SampleProfile/SampleProfile"
  ).then((module) => ({ default: module.GuestSampleProfile }))
);
const InfluencerTemporaryProfile = React.lazy(() =>
  import("./Pages/Influencer/TemporaryProfile/TempProfile").then((module) => ({
    default: module.InfluencerTemporaryProfile,
  }))
);
const PlatformCheck = React.lazy(() =>
  import("./Pages/Dashboard/Influencer/Platforms/PlatformCheck").then(
    (module) => ({ default: module.PlatformCheck })
  )
);
const SyncMyLinkedinUsingPlugin = React.lazy(() =>
  import("./Pages/Influencer/CreateProfileV1/SyncLinkedinWithExt").then(
    (module) => ({ default: module.SyncMyLinkedinUsingPlugin })
  )
);
const PluginCallback = React.lazy(() =>
  import("./Pages/Dashboard/Influencer/Plugin/Callback").then((module) => ({
    default: module.PluginCallback,
  }))
);
const PriceCalculator = React.lazy(() =>
  import("./Pages/Influencer/PriceCalculator/PriceCalculator").then(
    (module) => ({ default: module.PriceCalculator })
  )
);
const LocationBasedChapters = React.lazy(() =>
  import("./Pages/Influencer/LocationChapters/Chapters").then((module) => ({
    default: module.LocationBasedChapters,
  }))
);
const LocationBasedChaptersRedirect = React.lazy(() =>
  import("./Pages/Influencer/LocationChapters/Chapters").then((module) => ({
    default: module.LocationBasedChaptersRedirect,
  }))
);
const ExactPrice = React.lazy(() =>
  import("./Pages/Influencer/PriceCalculator/ExactPrice").then((module) => ({
    default: module.ExactPrice,
  }))
);
const AutoMode = React.lazy(() =>
  import("./Pages/Influencer/PriceCalculator/AutoMode").then((module) => ({
    default: module.AutoMode,
  }))
);
const PostEditor = React.lazy(() =>
  import("./Pages/Influencer/PostEditor/PostEditor").then((module) => ({
    default: module.PostEditor,
  }))
);
const CustomPricingPage = React.lazy(
  () => import("./Pages/Brand/Pricing/customPricing/CustomPricing")
);
const InfluencerSignupPage = React.lazy(
  () => import("./Pages/Influencer/InfluencerLogin/InfluencerLogin")
);
const SampleHome = React.lazy(
  () => import("./Pages/Brand/SampleCampaign/SampleLanding")
);
const AppSumoDiscountCodePage = React.lazy(
  () => import("./Pages/Brand/AppSumo/AppSumoDiscount")
);

const jwtTokenDeveloper = import.meta.env.VITE_JwtTokenDeveloper;

// Declare the EasebuzzCheckout property on the window object
declare global {
  interface Window {
    EasebuzzCheckout: any;
  }
}

// Create a reusable Suspense wrapper component
const SuspenseWrapper: React.FC<{
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ children, fallback = <LoaderOne /> }) => (
  <Suspense fallback={fallback}>{children}</Suspense>
);

const App: React.FC = () => {
  const [cookies] = useCookies();
  const location = useLocation();
  // mixpanel.init(import.meta.env.VITE_MIXPANELTOKEN, { debug: true });

  const authState = useAuth();
  const notificationState = useNotification();
  const generalState = useGeneralSettings();

  if (typeof window !== "undefined") {
    const doNotTrack =
      (navigator as any).doNotTrack ||
      (window as any)?.doNotTrack ||
      (navigator as any)?.msDoNotTrack;
    if (!doNotTrack) {
      mixpanel.init(import.meta.env.VITE_MIXPANELTOKEN);
    } else {
      mixpanel.init(import.meta.env.VITE_MIXPANELTOKEN);
      mixpanel.disable();
    }
  }

  // Utility function to check if localStorage is available
  function isLocalStorageAvailable() {
    try {
      const test = "test";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  // Function to load Easebuzz
  function loadEasebuzz() {
    return new Promise((resolve, reject) => {
      // Check if the script is already loaded
      if (document.getElementById("easebuzz-checkout-script")) {
        resolve(true);
        return;
      }

      // Create a script element
      const script = document.createElement("script");
      script.src =
        "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js";
      script.id = "easebuzz-checkout-script";
      script.onload = () => resolve(true);
      script.onerror = () =>
        reject(new Error("Failed to load Easebuzz script"));

      // Append the script to the document's head
      const head = document.getElementsByTagName("head")[0];
      head.appendChild(script);
    });
  }

  useEffect(() => {
    if (
      window.location.pathname?.toLowerCase()?.includes("/brand/dashboard") ||
      window.location.pathname?.toLowerCase()?.includes("/brand/pricing")
    ) {
      loadEasebuzz()
        .then(() => {})
        .catch((error) => {
          console.error("Error loading Easebuzz script", error);
        });
    }
  }, []);

  useEffect(() => {
    if (isLocalStorageAvailable()) {
      if (
        localStorage.getItem("jwtToken") &&
        localStorage.getItem("UserType") === "Brand"
      ) {
        authState?.getBrandData();
      } else if (localStorage.getItem("jwtToken")) {
        authState?.getUserData();
      }
      if (
        location?.pathname?.includes("/influencer") ||
        location?.pathname?.includes("/brand")
      ) {
        notificationState?.refreshNotifications();
      }
    }
  }, [location]);

  function ExternalInfluencerRedirect() {
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const referParam = params.get("refer");
      if (referParam) {
        if (referParam) {
          const referralData = {
            code: referParam,
            timestamp: Date.now(),
          };
          localStorage.setItem(
            "anchors_collab_refer",
            JSON.stringify(referralData)
          );
        }
      }

      window.location.href = referParam
        ? `https://www.anchors.in/influencer/?refer=${referParam}`
        : "https://www.anchors.in/influencer/";
    }, []);

    return null;
  }

  function ExternalBrandRedirect() {
    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const referParam = params.get("af_refer");

      if (referParam) {
        const referralData = {
          code: referParam,
          timestamp: Date.now(),
        };
        localStorage.setItem(
          "brand_affiliate_refer",
          JSON.stringify(referralData)
        );
      }
      window.location.href = referParam
        ? `https://www.anchors.in/linkedin-influencer-marketing-platform/?refer=${referParam}`
        : "https://www.anchors.in/linkedin-influencer-marketing-platform/";
    }, []);

    return null;
  }

  return (
    <>
      {generalState?.Loading && <LoaderOne />}
      {generalState?.DashboardLoading && <DashboardSkeleton />}
      {/* <ThemeProvider> */}
      <PaymentProvider>
        <CouponProvider>
          <ExtensionProvider>
            <StaticDataProvider>
              <InfluencerProvider>
                <BrandProvider>
                  <BrandDealsProvider>
                    <Routes>
                      <Route
                        path="/"
                        element={<ExternalInfluencerRedirect />}
                      />
                      <Route path="/check" element={<Check />} />
                      <Route
                        path="*"
                        element={<ExternalInfluencerRedirect />}
                      />
                      <Route
                        path="/influencer"
                        element={<ExternalInfluencerRedirect />}
                      />
                      <Route
                        path="/influencer/*"
                        element={
                          <ProtectedRoute
                            navigateCondition={
                              !isLocalStorageAvailable() ||
                              !localStorage.getItem("jwtToken")
                            }
                            toUrl={"/"}
                          >
                            <SuspenseWrapper>
                              <Home />
                            </SuspenseWrapper>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard"
                        element={<Navigate to={"/influencer"} />}
                      />

                      <Route
                        path="/influencer/login"
                        element={
                          <ProtectedRoute
                            navigateCondition={
                              isLocalStorageAvailable() &&
                              localStorage.getItem("jwtToken")?.length > 0 &&
                              localStorage.getItem("UserType") === "Influencer"
                            }
                            toUrl={"/influencer/dashboard"}
                          >
                            <SuspenseWrapper>
                              <InfluencerSignupPage />
                            </SuspenseWrapper>
                          </ProtectedRoute>
                        }
                      />

                      {/* ------- third party api success check  ------ */}
                      <Route
                        path="/platform/check"
                        element={
                          <SuspenseWrapper>
                            <PlatformCheck />
                          </SuspenseWrapper>
                        }
                      />

                      {/* Location based Chapter --------- */}
                      <Route
                        path="/chapter/:chapterSlug"
                        element={
                          <SuspenseWrapper>
                            <LocationBasedChapters />
                          </SuspenseWrapper>
                        }
                      />

                      <Route
                        path="/influencer/:chapterId/chapter"
                        element={
                          <SuspenseWrapper>
                            <LocationBasedChaptersRedirect />
                          </SuspenseWrapper>
                        }
                      />

                      <Route
                        path="/:slug"
                        element={
                          <SuspenseWrapper>
                            <InfluencerPublicProfile />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/t/:slug"
                        element={
                          <SuspenseWrapper>
                            <InfluencerTemporaryProfile />
                          </SuspenseWrapper>
                        }
                      />

                      {/* -------- dummy guest user profile ---------- */}
                      <Route
                        path="/influencer/preview/"
                        element={
                          <SuspenseWrapper>
                            <GuestSampleProfile />
                          </SuspenseWrapper>
                        }
                      />

                      {/* ------------------ plugin sync - linkedin ----------- */}
                      <Route
                        path="/influencer/plugin/sync-profile"
                        element={
                          <ProtectedRoute
                            navigateCondition={
                              !isLocalStorageAvailable() ||
                              !localStorage.getItem("jwtToken")
                            }
                            toUrl={"/"}
                          >
                            <SuspenseWrapper>
                              <SyncMyLinkedinUsingPlugin />
                            </SuspenseWrapper>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/influencer/plugin/callback"
                        element={
                          <SuspenseWrapper>
                            <PluginCallback />
                          </SuspenseWrapper>
                        }
                      />

                      {/* ------- Price Calculator ----------------------------- */}
                      <Route
                        path="/linkedin-pricing-calculator"
                        element={
                          <SuspenseWrapper>
                            <PriceCalculator />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/linkedin-pricing-calculator-auto-mode"
                        element={
                          <SuspenseWrapper>
                            <AutoMode />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/linkedin-pricing-calculator/u/:username"
                        element={
                          <SuspenseWrapper>
                            <ExactPrice />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/linkedin-post-preview"
                        element={
                          <SuspenseWrapper>
                            <PostEditor />
                          </SuspenseWrapper>
                        }
                      />

                      {/* Collab Verification Rating ----------------------------- */}
                      <Route
                        path="/brand/verify/:link"
                        element={
                          <SuspenseWrapper>
                            <CollabRatingForm />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/brand/rating/success"
                        element={
                          <SuspenseWrapper>
                            <CollabRatingSuccess />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/brand/appSumo/discount"
                        element={
                          <SuspenseWrapper>
                            <AppSumoDiscountCodePage />
                          </SuspenseWrapper>
                        }
                      />

                      {/* Brand routes .............................................. */}
                      <Route
                        path="/Brand"
                        element={<ExternalBrandRedirect />}
                      />
                      <Route
                        path="/Brand/*"
                        element={
                          <SuspenseWrapper>
                            <BrandRoutes />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/Brand/customPricing"
                        element={
                          <SuspenseWrapper>
                            <CustomPricingPage />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/brand/sampleCampaign"
                        element={
                          <SuspenseWrapper>
                            <SampleHome />
                          </SuspenseWrapper>
                        }
                      />

                      {/* Developer routes .......................................... */}
                      <Route
                        path="/developer/login"
                        element={
                          <SuspenseWrapper>
                            <AdminLogin />
                          </SuspenseWrapper>
                        }
                      />
                      <Route
                        path="/developer/admin/*"
                        element={
                          <ProtectedRoute
                            navigateCondition={
                              !isLocalStorageAvailable() ||
                              localStorage.getItem("jwtTokenD") !==
                                jwtTokenDeveloper ||
                              !localStorage.getItem("isDev") ||
                              !cookies.devsession
                            }
                            toUrl={"/developer/login"}
                          >
                            <SuspenseWrapper>
                              <AdminDashBoard />
                            </SuspenseWrapper>
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/logout"
                        element={
                          <ProtectedRoute
                            navigateCondition={
                              !isLocalStorageAvailable() ||
                              !localStorage.getItem("jwtToken")
                            }
                            toUrl={"/"}
                          >
                            <SuspenseWrapper>
                              <Logout />
                            </SuspenseWrapper>
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </BrandDealsProvider>
                </BrandProvider>
              </InfluencerProvider>
            </StaticDataProvider>
          </ExtensionProvider>
        </CouponProvider>
      </PaymentProvider>
      {/* </ThemeProvider> */}
      <ToastContainer position="top-center" limit={1} />
    </>
  );
};

export default App;
